import { TILE_SIZE, COLORS, TEXTURE_KEYS } from './constants.js';

// All visuals are procedurally generated (flat-vector, limited palette) so
// the project is playable with zero binary art assets. Swap any of these
// for real sprite sheets later without touching gameplay code — everything
// downstream references TEXTURE_KEYS, not raw graphics.
export function generateAllTextures(scene) {
  const g = scene.add.graphics();
  const T = TILE_SIZE;

  const gen = (key, w, h, draw) => {
    g.clear();
    draw(g, w, h);
    g.generateTexture(key, w, h);
  };

  // --- Tiles -----------------------------------------------------------
  // Drawn to match the comic-art floor/platform style (white panel, black
  // border, small square bolt marks) but generated per-cell like the
  // original procedural tiles — so the drawn rectangle IS the collision
  // rectangle, exactly, with no separate art-vs-physics alignment to fight.
  // (The loaded Floor_tile_*/Platform_1 art doesn't drive gameplay tiles
  // anymore for this reason — see Game.js's _buildTiles.)
  gen(TEXTURE_KEYS.TILE_SOLID, T, T, (gr) => {
    gr.fillStyle(0xffffff, 1);
    gr.fillRect(0, 0, T, T);
    gr.lineStyle(2, 0x000000, 1);
    gr.strokeRect(1, 1, T - 2, T - 2);
    // corner bolt marks
    gr.fillStyle(0x000000, 1);
    const b = 2;
    gr.fillRect(5, 5, b, b);
    gr.fillRect(T - 5 - b, 5, b, b);
    gr.fillRect(5, T - 5 - b, b, b);
    gr.fillRect(T - 5 - b, T - 5 - b, b, b);
  });

  // Tried giving solid cells buried under another solid cell (e.g. the
  // floor's 2nd row, kept purely for physics robustness) a plain/undecorated
  // look so they wouldn't show a seam against the tile above — abandoned:
  // white-on-white was invisible against the page background (looked like a
  // missing tile), and a distinct grey just looked like an unrelated
  // material. Every solid cell now uses the same bordered TILE_SOLID
  // unconditionally (see Game.js's _buildTiles) — a visible seam between
  // stacked floor tiles is just what a tiled floor looks like.
  /*
  gen(TEXTURE_KEYS.TILE_SOLID_FILL, T, T, (gr) => {
    gr.fillStyle(0xdcdcdc, 1);
    gr.fillRect(0, 0, T, T);
  });
  */

  // Same white/black bordered-panel family as TILE_SOLID, just shorter —
  // reads as the platform "shelf" version of the same material.
  gen(TEXTURE_KEYS.TILE_PLATFORM, T, T * 0.4, (gr, w, h) => {
    gr.fillStyle(0xffffff, 1);
    gr.fillRect(0, 0, w, h);
    gr.lineStyle(2, 0x000000, 1);
    gr.strokeRect(1, 1, w - 2, h - 2);
    gr.fillStyle(0x000000, 1);
    const b = 1.6;
    gr.fillRect(4, h / 2 - b / 2, b, b);
    gr.fillRect(w - 4 - b, h / 2 - b / 2, b, b);
  });

  // Comic-art overhaul: Fire.png (see Preload.js / Game.js's _buildTiles)
  // replaces this procedural red-triangle spike texture for '^' hazard
  // tiles. Left commented out, not deleted.
  /*
  gen(TEXTURE_KEYS.TILE_SPIKE, T, T, (gr, w, h) => {
    gr.fillStyle(COLORS.danger, 1);
    for (let i = 0; i < 3; i++) {
      const x0 = i * (w / 3);
      gr.fillTriangle(x0, h, x0 + w / 6, h * 0.15, x0 + w / 3, h);
    }
  });
  */

  // --- Player ------------------------------------------------------------
  // Comic-art overhaul: the player now uses loaded pose images (see
  // Preload.js / Player.js: main_idle/run/jump/shoot under TEXTURE_KEYS.
  // PLAYER/PLAYER_RUN/PLAYER_JUMP/PLAYER_SHOOT) instead of this procedural
  // sprite. Left commented out, not deleted, in case art is ever missing
  // and a fallback is needed.
  /*
  gen(TEXTURE_KEYS.PLAYER, 22, 30, (gr, w, h) => {
    const headW = 12;
    const headX = (w - headW) / 2;
    const headY = 3;
    const headH = 11;
    const torsoY = 13;
    const torsoH = 13;
    const bootY = 26;
    const bootH = 4;

    // contour behind both shapes
    gr.fillStyle(0x040108, 1);
    gr.fillRoundedRect(headX - 1, headY - 1, headW + 2, headH + 2, 5);
    gr.fillRoundedRect(1, torsoY, w - 2, torsoH + 1, 4);

    // backpack strap hint on the trailing edge
    gr.fillStyle(COLORS.playerArmorDark, 1);
    gr.fillRoundedRect(1, torsoY + 2, 5, torsoH - 3, 2);

    // torso — dark tactical armor
    gr.fillStyle(COLORS.playerArmor, 1);
    gr.fillRoundedRect(2, torsoY + 1, w - 4, torsoH - 1, 3);
    gr.fillStyle(0x000000, 0.25);
    gr.fillRoundedRect(2, torsoY + 1, 5, torsoH - 1, 3);
    // chest circuit glow
    gr.fillStyle(COLORS.cyan, 0.85);
    gr.fillRect(w / 2 - 1, torsoY + 3, 2, torsoH - 6);

    // cybernetic forearm on the leading (right) side
    gr.fillStyle(COLORS.playerRig, 1);
    gr.fillRoundedRect(w - 7, torsoY + 2, 5, torsoH - 4, 2);
    gr.fillStyle(COLORS.cyan, 0.9);
    gr.fillRect(w - 6, torsoY + 5, 3, 1.5);

    // hood — dark, a little wider than the torso top; eyes glow within
    gr.fillStyle(0x0a0814, 1);
    gr.fillRoundedRect(headX - 2, headY - 1, headW + 4, headH + 3, 6);
    gr.fillStyle(COLORS.cyan, 0.9);
    gr.fillRect(headX + 2, headY + 5, headW - 4, 2);
    gr.fillStyle(0xffffff, 0.5);
    gr.fillRect(headX + 2, headY + 5, 2, 2);

    // boots, cyan sole trim
    gr.fillStyle(COLORS.playerArmorDark, 1);
    gr.fillRect(0, bootY, w, bootH);
    gr.fillStyle(COLORS.cyan, 0.85);
    gr.fillRect(0, bootY + bootH - 1.5, w, 1.5);
  });
  */

  // --- Projectiles -----------------------------------------------------
  // Sized as a clear dash/beam ("---") rather than a small dot, so they're
  // readable at a glance and easy to track/dodge. (Was 52px long — trimmed
  // down a bit, keeping the leading-tip proportion the same.)
  gen(TEXTURE_KEYS.PLAYER_PROJECTILE, 36, 8, (gr, w, h) => {
    gr.fillStyle(COLORS.cyan, 0.35);
    gr.fillRoundedRect(0, 1, w, h - 2, 3);
    gr.fillStyle(COLORS.cyan, 1);
    gr.fillRoundedRect(2, 2, w - 4, h - 4, 2);
    gr.fillStyle(COLORS.white, 1);
    gr.fillRoundedRect(w - 10, 2, 10, h - 4, 2);
  });

  gen(TEXTURE_KEYS.ENEMY_PROJECTILE, 36, 8, (gr, w, h) => {
    gr.fillStyle(COLORS.danger, 0.35);
    gr.fillRoundedRect(0, 1, w, h - 2, 3);
    gr.fillStyle(COLORS.danger, 1);
    gr.fillRoundedRect(2, 2, w - 4, h - 4, 2);
    gr.fillStyle(COLORS.warnYellow, 0.9);
    gr.fillRoundedRect(2, 2, 10, h - 4, 2);
  });

  // --- Enemies -------------------------------------------------------------
  // Comic-art overhaul: all enemy textures (3 soldiers, 2 drones, turret,
  // boss) now load real art in Preload.js under the same TEXTURE_KEYS used
  // below, so none of this procedural generation runs anymore. Left
  // commented out (not deleted) in case art is missing for a given key or
  // more variants come back later — see the matching commented-out configs
  // in Enemy.js's ENEMY_CONFIGS.
  /*
  // Shared trooper chassis. Earlier version drew the whole body as one
  // uniform-width blob with a visor stripe partway down — which reads as
  // headless, since nothing narrows above the "shoulders". This version
  // draws a genuinely separate, narrower head shape stacked on a wider
  // torso, like a normal character silhouette. All soldier() calls here
  // use h=30, so the head/torso/boot bands below are fixed pixel rows;
  // only `w` (torso width) varies per variant.
  const HEAD_W = 12;
  const HEAD_Y = 3;
  const HEAD_H = 11; // head occupies y:[3,14]
  const TORSO_Y = 13; // 1px overlap with head bottom for a seamless join
  const TORSO_H = 13; // torso occupies y:[13,26]
  const BOOT_Y = 26; // boots occupy y:[26,30]
  const BOOT_H = 4;

  function soldier(key, w, h, { body, eye = COLORS.danger, eyeCount = 1, shoulder, boot = 0x2a1408, hooded = false, antenna = false, extra } = {}) {
    const headX = (w - HEAD_W) / 2;
    gen(key, w, h, (gr) => {
      // contour behind both shapes
      gr.fillStyle(0x040108, 1);
      gr.fillRoundedRect(headX - 1, HEAD_Y - 1, HEAD_W + 2, HEAD_H + 2, 5);
      gr.fillRoundedRect(1, TORSO_Y, w - 2, TORSO_H + 1, 4);

      // torso
      gr.fillStyle(body, 1);
      gr.fillRoundedRect(2, TORSO_Y + 1, w - 4, TORSO_H - 1, 3);
      gr.fillStyle(0x000000, 0.2);
      gr.fillRoundedRect(2, TORSO_Y + 1, 5, TORSO_H - 1, 3);
      gr.fillStyle(0xffffff, 0.12);
      gr.fillRoundedRect(w - 7, TORSO_Y + 1, 4, TORSO_H - 1, 3);

      // shoulders flare out beyond the head width, right below it
      if (shoulder && !hooded) {
        gr.fillStyle(shoulder, 1);
        gr.fillRoundedRect(0, TORSO_Y + 1, 5, 7, 2);
        gr.fillRoundedRect(w - 5, TORSO_Y + 1, 5, 7, 2);
      }

      // head — a distinctly narrower shape stacked above the torso
      if (hooded) {
        gr.fillStyle(0x040108, 1);
        gr.fillRoundedRect(headX - 2, HEAD_Y - 1, HEAD_W + 4, HEAD_H + 5, 6);
      } else {
        gr.fillStyle(body, 1);
        gr.fillRoundedRect(headX, HEAD_Y, HEAD_W, HEAD_H, 5);
        gr.fillStyle(COLORS.black, 1);
        gr.fillRect(headX + 1, HEAD_Y + HEAD_H - 6, HEAD_W - 2, 4);
      }

      if (antenna) {
        gr.fillStyle(0x2a2438, 1);
        gr.fillRect(headX + HEAD_W - 3, 0, 1.5, HEAD_Y);
        gr.fillStyle(eye, 1);
        gr.fillCircle(headX + HEAD_W - 2.3, 0, 1.2);
      }

      const eyeY = hooded ? HEAD_Y + 5 : HEAD_Y + HEAD_H - 5;
      if (eyeCount <= 1) {
        gr.fillStyle(eye, 0.9);
        gr.fillRect(headX + HEAD_W - 5, eyeY, 3, 3);
      } else {
        const span = HEAD_W - 4;
        for (let i = 0; i < eyeCount; i++) {
          gr.fillStyle(eye, 0.9);
          gr.fillRect(headX + 2 + (span / (eyeCount - 1)) * i, eyeY, 2.5, 2.5);
        }
      }

      gr.fillStyle(boot, 1);
      gr.fillRect(0, BOOT_Y, w, BOOT_H);

      if (extra) extra(gr, w, h);
    });
  }

  // 01 Standard — the baseline trooper.
  soldier(TEXTURE_KEYS.SOLDIER, 22, 30, { body: COLORS.soldierBody });

  // 02 Scout — pale light armor, single red visor, faster/fragile.
  soldier(TEXTURE_KEYS.SOLDIER_SCOUT, 22, 30, {
    body: COLORS.soldierScoutBody,
    shoulder: COLORS.soldierScoutBody,
    boot: 0x555568
  });

  // 03 Recon — dark blue-grey armor, antenna, triple-lens visor (wide sight).
  soldier(TEXTURE_KEYS.SOLDIER_RECON, 22, 30, {
    body: COLORS.soldierReconBody,
    eyeCount: 3,
    shoulder: COLORS.soldierReconBody,
    boot: 0x1c2436,
    antenna: true
  });

  // 04 Assault — deep red/black armor with a chest trim stripe.
  soldier(TEXTURE_KEYS.SOLDIER_ASSAULT, 22, 30, {
    body: COLORS.soldierAssaultBody,
    shoulder: COLORS.danger,
    boot: 0x220608,
    extra: (gr, w, h) => {
      gr.fillStyle(COLORS.danger, 0.55);
      gr.fillRect(4, h - 12, w - 8, 1.5);
    }
  });

  // 05 Shield — riot shield held to the front (right) side, tanky/slow.
  soldier(TEXTURE_KEYS.SOLDIER_SHIELD, 26, 30, {
    body: COLORS.soldierShieldBody,
    shoulder: COLORS.soldierShieldBody,
    boot: 0x1c1c24,
    extra: (gr, w, h) => {
      gr.fillStyle(0x040108, 1);
      gr.fillRoundedRect(w - 7, 13, 7, h - 19, 2);
      gr.fillStyle(0x3c3c48, 1);
      gr.fillRoundedRect(w - 6, 14, 5, h - 21, 2);
      gr.fillStyle(COLORS.danger, 0.8);
      gr.fillRect(w - 5, h / 2 - 1, 3, 3);
    }
  });

  // 06 Stealth — hooded silhouette, eyes glow from under the hood, cloak hint.
  soldier(TEXTURE_KEYS.SOLDIER_STEALTH, 22, 30, {
    body: COLORS.soldierStealthBody,
    hooded: true,
    boot: 0x0c0810,
    extra: (gr, w, h) => {
      gr.fillStyle(0x000000, 0.35);
      gr.fillTriangle(1, 14, w - 1, 14, w / 2, h - 6);
    }
  });

  // 07 Heavy — bulkier frame, minigun cluster bolted to the right side.
  soldier(TEXTURE_KEYS.SOLDIER_HEAVY, 26, 30, {
    body: COLORS.soldierHeavyBody,
    shoulder: COLORS.soldierHeavyBody,
    boot: 0x1c1c24,
    extra: (gr, w, h) => {
      gr.fillStyle(0x040108, 1);
      gr.fillRoundedRect(w - 8, h * 0.5 - 4, 8, 8, 2);
      gr.fillStyle(0x1c1c24, 1);
      gr.fillCircle(w - 2, h * 0.5 - 2, 1.3);
      gr.fillCircle(w - 2, h * 0.5 + 2, 1.3);
      gr.fillStyle(COLORS.danger, 0.5);
      gr.fillRect(w - 8, h * 0.5 + 5, 8, 1.5);
    }
  });

  // 08 Elite — dark navy armor, cyan trim/visor/antenna, toughest trooper.
  soldier(TEXTURE_KEYS.SOLDIER_ELITE, 22, 30, {
    body: COLORS.soldierEliteBody,
    eye: COLORS.cyan,
    shoulder: COLORS.soldierEliteBody,
    boot: 0x0c1420,
    antenna: true,
    extra: (gr, w, h) => {
      gr.lineStyle(1, COLORS.cyan, 0.8);
      gr.strokeRoundedRect(2, 14, w - 4, 12, 3);
    }
  });

  // Shared drone chassis: dark contour, rounded body, top highlight, side
  // pods, a central "eye" lens, and a bottom thruster glow. Each variant
  // below passes its own palette plus an optional `extra` callback for
  // type-specific silhouette add-ons (wings, dish, spikes, alert marks) so
  // all 8 read as one family while staying visually distinct at a glance.
  // NOTE: generateTexture() captures exactly the [0,w]x[0,h] box — anything
  // drawn at negative coordinates or beyond w/h is silently clipped, not
  // wrapped or auto-expanded. Every shape below is kept inside that box
  // (the body itself only occupies x:[2,w-4], y:[4,h-8], leaving a few
  // pixels of margin on each edge for pods/antenna/trim to live in).
  function drone(key, w, h, { body, eye, thruster, pods = true, antenna = true, extra } = {}) {
    gen(key, w, h, (gr) => {
      gr.fillStyle(0x040108, 1);
      gr.fillRoundedRect(1, 3, w - 2, h - 6, 7);
      gr.fillStyle(body, 1);
      gr.fillRoundedRect(2, 4, w - 4, h - 8, 6);
      gr.fillStyle(0xffffff, 0.25);
      gr.fillRoundedRect(4, 4, w - 8, 3, 3);
      gr.fillStyle(0x000000, 0.2);
      gr.fillRoundedRect(4, h - 8, w - 8, 3, 3);
      if (pods) {
        gr.fillStyle(0x040108, 1);
        gr.fillRoundedRect(0, h * 0.35, 4, h * 0.4, 2);
        gr.fillRoundedRect(w - 4, h * 0.35, 4, h * 0.4, 2);
        gr.fillStyle(body, 1);
        gr.fillRoundedRect(0, h * 0.38, 3, h * 0.34, 2);
        gr.fillRoundedRect(w - 3, h * 0.38, 3, h * 0.34, 2);
      }
      if (antenna) {
        gr.fillStyle(0x2a2438, 1);
        gr.fillRect(w / 2 - 1, 0, 2, 3);
        gr.fillStyle(eye, 1);
        gr.fillCircle(w / 2, 0, 1.4);
      }
      // eye / lens
      gr.fillStyle(COLORS.black, 1);
      gr.fillCircle(w / 2, h / 2, 5);
      gr.fillStyle(eye, 1);
      gr.fillCircle(w / 2, h / 2, 3);
      gr.fillStyle(0xffffff, 0.7);
      gr.fillCircle(w / 2 - 1, h / 2 - 1, 1);
      // thruster
      gr.fillStyle(COLORS.black, 0.5);
      gr.fillRect(w / 2 - (w - 8) / 2, h - 4, w - 8, 2);
      gr.fillStyle(thruster, 0.7);
      gr.fillTriangle(w / 2 - 5, h - 2, w / 2 + 5, h - 2, w / 2, h + 5);
      if (extra) extra(gr, w, h);
    });
  }

  // 01 Standard — the baseline patrol unit.
  drone(TEXTURE_KEYS.DRONE, 28, 20, { body: COLORS.droneBody, eye: COLORS.danger, thruster: COLORS.cyan });

  // 02 Heavy — bulkier chassis, thicker pods, bigger red eye, no antenna.
  drone(TEXTURE_KEYS.DRONE_HEAVY, 34, 26, {
    body: COLORS.droneHeavyBody,
    eye: COLORS.danger,
    thruster: COLORS.danger,
    antenna: false,
    extra: (gr, w, h) => {
      gr.fillStyle(0x040108, 1);
      gr.fillRoundedRect(0, h * 0.3, 6, h * 0.5, 2);
      gr.fillRoundedRect(w - 6, h * 0.3, 6, h * 0.5, 2);
      gr.fillStyle(COLORS.droneHeavyBody, 1);
      gr.fillRoundedRect(1, h * 0.33, 4, h * 0.44, 2);
      gr.fillRoundedRect(w - 5, h * 0.33, 4, h * 0.44, 2);
      gr.fillStyle(COLORS.danger, 0.6);
      gr.fillRect(4, 6, w - 8, 1.5);
    }
  });

  // 03 Scout — lighter, leaner, pale hull, blue lens.
  drone(TEXTURE_KEYS.DRONE_SCOUT, 24, 17, {
    body: COLORS.droneScoutBody,
    eye: COLORS.cyan,
    thruster: COLORS.cyan
  });

  // 04 Stealth — near-black hull, violet lens/antenna/thruster.
  drone(TEXTURE_KEYS.DRONE_STEALTH, 28, 20, {
    body: COLORS.droneStealthBody,
    eye: COLORS.droneStealthAccent,
    thruster: COLORS.droneStealthAccent,
    extra: (gr, w, h) => {
      gr.fillStyle(COLORS.droneStealthAccent, 0.35);
      gr.fillRoundedRect(2, 4, w - 4, h - 8, 6);
    }
  });

  // 05 Shield — standard hull flanked by two energy-wing panels.
  drone(TEXTURE_KEYS.DRONE_SHIELD, 34, 20, {
    body: COLORS.droneBody,
    eye: COLORS.cyan,
    thruster: COLORS.cyan,
    pods: false,
    antenna: false,
    extra: (gr, w, h) => {
      gr.fillStyle(COLORS.cyan, 0.35);
      gr.fillEllipse(3, h / 2, 6, h - 2);
      gr.fillEllipse(w - 3, h / 2, 6, h - 2);
      gr.lineStyle(1.5, COLORS.cyan, 0.9);
      gr.strokeEllipse(3, h / 2, 6, h - 2);
      gr.strokeEllipse(w - 3, h / 2, 6, h - 2);
    }
  });

  // 06 Hacker — green lens, satellite dish instead of a plain antenna.
  drone(TEXTURE_KEYS.DRONE_HACKER, 28, 20, {
    body: COLORS.droneBody,
    eye: COLORS.droneHackerAccent,
    thruster: COLORS.droneHackerAccent,
    antenna: false,
    extra: (gr, w, h) => {
      gr.fillStyle(0x2a2438, 1);
      gr.fillRect(w / 2 + 3, 0, 1.5, 3);
      gr.fillStyle(COLORS.droneScoutBody, 1);
      gr.fillEllipse(w / 2 + 6, 1, 7, 3);
      gr.fillStyle(COLORS.droneHackerAccent, 0.85);
      gr.fillCircle(w / 2 + 6, 1, 1.2);
    }
  });

  // 07 Elite — dark armor plating with gold trim, spiked pods.
  drone(TEXTURE_KEYS.DRONE_ELITE, 32, 24, {
    body: COLORS.droneHeavyBody,
    eye: COLORS.droneEliteAccent,
    thruster: COLORS.droneEliteAccent,
    pods: false,
    antenna: false,
    extra: (gr, w, h) => {
      gr.lineStyle(1.5, COLORS.droneEliteAccent, 0.9);
      gr.strokeRoundedRect(2, 4, w - 4, h - 8, 6);
      gr.fillStyle(0x040108, 1);
      gr.fillTriangle(0, h * 0.5, 4, h * 0.35, 4, h * 0.65);
      gr.fillTriangle(w, h * 0.5, w - 4, h * 0.35, w - 4, h * 0.65);
      gr.fillStyle(COLORS.droneEliteAccent, 1);
      gr.fillTriangle(w / 2, 0, w / 2 - 3, 4, w / 2 + 3, 4);
    }
  });

  // 08 Alert — standard palette, flanked by warning chevrons (spotted state).
  drone(TEXTURE_KEYS.DRONE_ALERT, 28, 20, {
    body: COLORS.droneScoutBody,
    eye: COLORS.danger,
    thruster: COLORS.danger,
    antenna: false,
    extra: (gr, w, h) => {
      // warning marks tucked just inside the top corners, in the same
      // margin the antenna nub normally uses
      gr.fillStyle(COLORS.danger, 1);
      gr.fillRect(2, 0, 2, 3);
      gr.fillRect(w - 4, 0, 2, 3);
      gr.fillCircle(3, 4.5, 1);
      gr.fillCircle(w - 3, 4.5, 1);
    }
  });

  gen(TEXTURE_KEYS.TURRET, 26, 26, (gr, w, h) => {
    gr.fillStyle(0x040108, 1);
    gr.fillRoundedRect(1, h * 0.38, w - 2, h * 0.6, 4);
    gr.fillStyle(COLORS.turretBody, 1);
    gr.fillRoundedRect(2, h * 0.4, w - 4, h * 0.6 - 2, 3);
    gr.fillStyle(0x000000, 0.2);
    gr.fillRect(2, h - 6, w - 4, 4);
    gr.fillStyle(COLORS.danger, 1);
    gr.fillCircle(w / 2, h * 0.35, 6);
    gr.fillStyle(0xffffff, 0.3);
    gr.fillCircle(w / 2 - 2, h * 0.32, 2);
    gr.fillStyle(COLORS.black, 1);
    gr.fillRect(w / 2 - 2, h * 0.1, 4, h * 0.3);
    gr.fillStyle(COLORS.warnYellow, 0.7);
    gr.fillRect(3, h * 0.42, w - 6, 1.5);
  });

  gen(TEXTURE_KEYS.BOSS, 64, 72, (gr, w, h) => {
    gr.fillStyle(0x040108, 1);
    gr.fillRoundedRect(2, 4, w - 4, h - 8, 7);
    gr.fillStyle(COLORS.bossBody, 1);
    gr.fillRoundedRect(4, 6, w - 8, h - 12, 6);
    gr.fillStyle(0x000000, 0.22);
    gr.fillRoundedRect(4, 6, 10, h - 12, 6);
    gr.fillStyle(0xffffff, 0.15);
    gr.fillRoundedRect(w - 16, 6, 8, h - 12, 6);
    // shoulder plating
    gr.fillStyle(0x7a1670, 1);
    gr.fillRoundedRect(2, 10, 14, 16, 3);
    gr.fillRoundedRect(w - 16, 10, 14, 16, 3);
    // visor / eyes
    gr.fillStyle(COLORS.black, 1);
    gr.fillRect(w / 2 - 14, 18, 28, 10);
    gr.fillStyle(COLORS.danger, 1);
    gr.fillCircle(w / 2 - 8, 23, 3);
    gr.fillCircle(w / 2 + 8, 23, 3);
    gr.fillStyle(0xffffff, 0.7);
    gr.fillCircle(w / 2 - 9, 22, 1);
    gr.fillCircle(w / 2 + 7, 22, 1);
    // chest core glow
    gr.fillStyle(COLORS.danger, 0.85);
    gr.fillCircle(w / 2, h * 0.62, 6);
    gr.fillStyle(COLORS.warnYellow, 0.6);
    gr.fillCircle(w / 2, h * 0.62, 2.5);
  });
  */

  // --- Hazards / pickups / world ------------------------------------------
  // Comic-art overhaul: Fence.png replaces this procedural texture (see
  // Preload.js / Hazard.js). Left commented out, not deleted.
  /*
  gen(TEXTURE_KEYS.HAZARD_FENCE, T, T, (gr, w, h) => {
    gr.lineStyle(2, COLORS.warnYellow, 1);
    for (let i = 0; i < 4; i++) {
      gr.beginPath();
      gr.moveTo(i * (w / 3), 0);
      gr.lineTo((i + 1) * (w / 3), h);
      gr.strokePath();
    }
    gr.fillStyle(COLORS.warnYellow, 0.08);
    gr.fillRect(0, 0, w, h);
  });

  gen(TEXTURE_KEYS.PICKUP_HEALTH, 18, 18, (gr, w, h) => {
    gr.fillStyle(COLORS.black, 1);
    gr.fillCircle(w / 2, h / 2, w / 2);
    gr.fillStyle(COLORS.magenta, 1);
    gr.fillRect(w / 2 - 6, h / 2 - 1.5, 12, 3);
    gr.fillRect(w / 2 - 1.5, h / 2 - 6, 3, 12);
  });

  // Collectible circuit-chip: a small IC silhouette with pins, distinct
  // from the health cross so it clearly reads as a score pickup.
  gen(TEXTURE_KEYS.PICKUP_CHIP, 16, 16, (gr, w, h) => {
    gr.fillStyle(0x0b0714, 1);
    gr.fillRoundedRect(3, 3, w - 6, h - 6, 2);
    gr.lineStyle(1.5, COLORS.warnYellow, 1);
    gr.strokeRoundedRect(3, 3, w - 6, h - 6, 2);
    gr.fillStyle(COLORS.warnYellow, 0.9);
    gr.fillRect(0, 4, 3, 2);
    gr.fillRect(0, h - 6, 3, 2);
    gr.fillRect(w - 3, 4, 3, 2);
    gr.fillRect(w - 3, h - 6, 3, 2);
    gr.fillRect(4, 0, 2, 3);
    gr.fillRect(w - 6, 0, 2, 3);
    gr.fillRect(4, h - 3, 2, 3);
    gr.fillRect(w - 6, h - 3, 2, 3);
    gr.fillStyle(COLORS.cyan, 0.9);
    gr.fillRect(w / 2 - 2, h / 2 - 2, 4, 4);
  });

  // Ammo magazine: dark clip body, brass cartridge-tip band up top, glowing
  // cyan baseplate — distinct silhouette from the health cross and chip.
  gen(TEXTURE_KEYS.PICKUP_AMMO, 14, 18, (gr, w, h) => {
    gr.fillStyle(0x040108, 1);
    gr.fillRoundedRect(1, 1, w - 2, h - 2, 2);
    gr.fillStyle(0x2a2438, 1);
    gr.fillRoundedRect(2, 2, w - 4, h - 4, 2);
    gr.fillStyle(COLORS.warnYellow, 1);
    gr.fillRect(3, 3, w - 6, 3);
    gr.fillStyle(0xffffff, 0.3);
    gr.fillRect(3, 3, w - 6, 1);
    gr.fillStyle(COLORS.cyan, 0.9);
    gr.fillRect(3, h - 5, w - 6, 2);
  });
  */

  gen(TEXTURE_KEYS.PARTICLE, 6, 6, (gr, w, h) => {
    gr.fillStyle(COLORS.white, 1);
    gr.fillCircle(w / 2, h / 2, w / 2);
  });

  // --- Parallax skyline layers ---------------------------------------------
  // Comic-art overhaul: replaced by the layer_0_*/layer_3_* building art
  // composited in Game.js's _buildParallax() (see BG_FAR_KEYS/BG_NEAR_KEYS
  // in constants.js and Preload.js). Left commented out, not deleted.
  /*
  gen(TEXTURE_KEYS.BG_FAR, 320, 320, (gr, w, h) => {
    gr.fillStyle(0x1c1030, 1);
    gr.fillRect(0, 0, w, h);
    let x = 0;
    let i = 0;
    while (x < w) {
      const bw = 24 + ((i * 37) % 40);
      const bh = 60 + ((i * 53) % 140);
      gr.fillStyle(0x2e1850, 1);
      gr.fillRect(x, h - bh, bw, bh);
      if (i % 3 === 0) {
        gr.fillStyle(COLORS.purpleLight, 0.4);
        gr.fillRect(x + 4, h - bh + 10, 3, 3);
        gr.fillRect(x + bw - 8, h - bh + 22, 3, 3);
      }
      x += bw + 6;
      i++;
    }
  });

  gen(TEXTURE_KEYS.BG_NEAR, 320, 320, (gr, w, h) => {
    let x = 0;
    let i = 0;
    while (x < w) {
      const bw = 30 + ((i * 29) % 50);
      const bh = 90 + ((i * 71) % 180);
      gr.fillStyle(COLORS.purple, 1);
      gr.fillRect(x, h - bh, bw, bh);
      gr.fillStyle(i % 2 === 0 ? COLORS.cyan : COLORS.magenta, 0.5);
      for (let wy = h - bh + 8; wy < h - 6; wy += 14) {
        gr.fillRect(x + 5, wy, 4, 4);
        if (bw > 18) gr.fillRect(x + bw - 9, wy, 4, 4);
      }
      x += bw + 4;
      i++;
    }
  });
  */

  g.destroy();
}
